import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Download, FileText, UploadCloud, Loader2 } from "lucide-react";

const DOC_TYPES = [
  "GST Certificate",
  "PAN",
  "Business Registration",
  "Udyam / MSME Certificate",
  "Address Proof",
  "Bank Proof / Cancelled Cheque",
  "Trade / Business Licence",
  "Technician Photo ID",
  "Insurance",
  "Other",
];

export const ApplicationDocuments = ({ className = "" }) => {
  const [appId, setAppId] = useState(null);
  const [status, setStatus] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadDocs = (id) => api.get(`/my-application/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/my-application").then((r) => {
      const app = (r.data || [])[0];
      if (app) { setAppId(app.application_id); setStatus(app.status); loadDocs(app.application_id); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file || !appId) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType);
    try {
      await api.post(`/my-application/${appId}/documents`, fd);
      toast.success("Document uploaded for review");
      loadDocs(appId);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  if (loading) return null;
  if (!appId) return null;

  return (
    <div className={`bg-card border rounded-xl p-5 ${className}`} data-testid="application-documents">
      <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4 text-primary" /><h3 className="font-display font-bold">Verification Documents</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Upload your KYC and verification records for private review by authorized staff.</p>
      {status === "DOCUMENTS_REQUESTED" && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3 text-sm mb-4" data-testid="docs-requested-banner">
          Our team has requested additional documents. Please upload them below.
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-sm mb-1 block">Document Type</Label>
          <select className="border rounded-md h-10 px-3 text-sm w-full" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="appdoc-type">
            {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-primary text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90" data-testid="appdoc-upload-label">
          <input accept=".pdf,image/*" type="file" hidden onChange={upload} data-testid="appdoc-upload" />
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {busy ? "Uploading..." : "Upload"}
        </label>
      </div>
      <div className="mt-4 space-y-2">
        {docs.length === 0 ? <p className="text-sm text-muted-foreground">No documents uploaded yet.</p> :
          docs.map((d) => (
            <a key={d.doc_id} href={`${API}/application-documents/${d.doc_id}/file`} target="_blank" rel="noreferrer"
              className="flex items-center justify-between border rounded-lg p-2.5 text-sm hover:border-primary" data-testid={`appdoc-${d.doc_id}`}>
              <span><b>{d.doc_type}</b> <span className="text-muted-foreground text-xs">· {d.filename}</span></span>
              <Download className="w-4 h-4 text-muted-foreground" />
            </a>
          ))}
      </div>
    </div>
  );
};
